import React, { useState } from 'react';
import { X, Sparkles, Loader2, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { popAIScore } from '../../lib/popApi';

const BAND_META = {
  excellent: { color: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', label: 'Excellent fit' },
  strong:    { color: 'text-cyan-300',    bg: 'bg-cyan-500/15',    border: 'border-cyan-500/30',    label: 'Strong fit' },
  moderate:  { color: 'text-amber-300',   bg: 'bg-amber-500/15',   border: 'border-amber-500/30',   label: 'Moderate fit' },
  weak:      { color: 'text-orange-300',  bg: 'bg-orange-500/15',  border: 'border-orange-500/30',  label: 'Weak fit' },
  misfit:    { color: 'text-rose-300',    bg: 'bg-rose-500/15',    border: 'border-rose-500/30',    label: 'Misfit' },
};

const AIScoreModal = ({ opportunityId, opportunityTitle, onClose }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const run = async () => {
    setRunning(true); setErr(''); setResult(null);
    try {
      const r = await popAIScore(opportunityId);
      setResult(r.score);
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.detail) || 'AI scoring failed');
    } finally { setRunning(false); }
  };

  const band = result?.band && BAND_META[result.band] ? BAND_META[result.band] : BAND_META.moderate;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="ai-score-modal">
      <div className="relative max-w-lg w-full rounded-2xl border border-white/10 bg-slate-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-white" aria-label="Close">
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] font-bold text-emerald-300">AI PROFILE SCORING</div>
            <div className="text-sm font-serif font-semibold text-white leading-tight">{opportunityTitle}</div>
          </div>
        </div>
        <p className="text-xs text-white/50 mt-2 mb-4">GPT-6 Astra analyses your profile against the role and returns an honest fit assessment with strengths, gaps and next steps.</p>

        {!result && !running && (
          <button onClick={run} data-testid="ai-score-run"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold">
            <Sparkles className="w-4 h-4" /> Run AI Match Score
          </button>
        )}

        {running && (
          <div className="rounded-lg border border-white/10 bg-slate-900/60 p-4 text-center">
            <Loader2 className="w-6 h-6 text-amber-300 animate-spin mx-auto mb-2" />
            <div className="text-sm text-white/70">Analysing your profile against this opportunity…</div>
            <div className="text-[11px] text-white/40 mt-1">Typically 5-15 seconds</div>
          </div>
        )}

        {err && (
          <div className="mt-2 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-200 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {err}
          </div>
        )}

        {result && (
          <div className="space-y-4">
            {/* Score header */}
            <div className={`rounded-xl border ${band.border} ${band.bg} p-4 flex items-center gap-4`}>
              <div className={`text-4xl font-serif font-bold tabular-nums ${band.color}`}>{result.score}</div>
              <div>
                <div className={`text-[10px] tracking-[0.25em] font-bold uppercase ${band.color}`}>{band.label}</div>
                <div className="text-xs text-white/70 mt-1">{result.recommendation}</div>
              </div>
            </div>

            {/* Strengths & Gaps */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
                <div className="text-[10px] tracking-[0.25em] font-bold text-emerald-300 mb-2">STRENGTHS</div>
                <ul className="space-y-1.5">
                  {(result.strengths || []).map((s, i) => (
                    <li key={i} className="text-[11px] text-white/85 flex items-start gap-1.5">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <div className="text-[10px] tracking-[0.25em] font-bold text-amber-300 mb-2">GAPS</div>
                <ul className="space-y-1.5">
                  {(result.gaps || []).map((g, i) => (
                    <li key={i} className="text-[11px] text-white/85 flex items-start gap-1.5">
                      <AlertCircle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" /> {g}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Next steps */}
            {result.next_steps_for_candidate?.length > 0 && (
              <div className="rounded-lg border border-white/10 bg-slate-900/60 p-3">
                <div className="text-[10px] tracking-[0.25em] font-bold text-white/60 mb-2">RECOMMENDED NEXT STEPS</div>
                <ul className="space-y-1.5">
                  {result.next_steps_for_candidate.map((s, i) => (
                    <li key={i} className="text-[11px] text-white/85 flex items-start gap-1.5">
                      <ArrowRight className="w-3 h-3 text-amber-300 mt-0.5 flex-shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIScoreModal;
